import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/utils/toast'

import { legalService } from '@/services/legal'
import type { AcceptanceMethod, LegalDocumentType } from '@/types/legal'

export const LEGAL_PENDING_QUERY_KEY = ['legal', 'pending-acceptance'] as const

interface UseAcceptLegalOptions {
  onSuccess?: () => void
}

/**
 * Hook para verificar se há documentos legais pendentes de aceite
 */
export function usePendingAcceptance(enabled = true) {
  return useQuery({
    queryKey: LEGAL_PENDING_QUERY_KEY,
    queryFn: () => legalService.getPendingAcceptance(),
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook para aceitar um documento legal
 */
export function useAcceptLegal(options?: UseAcceptLegalOptions) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      documentVersionId,
      acceptanceMethod,
    }: {
      documentVersionId: string
      acceptanceMethod: AcceptanceMethod
    }) =>
      legalService.accept({
        documentVersionId,
        acceptanceMethod,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEGAL_PENDING_QUERY_KEY })
      toast.success('Termos aceitos com sucesso!')
      options?.onSuccess?.()
    },
    onError: () => {
      toast.error('Erro ao aceitar os termos. Tente novamente.')
    },
  })
}

/**
 * Hook para buscar o documento legal completo
 */
export function useLegalDocument(type: LegalDocumentType, enabled = true) {
  return useQuery({
    queryKey: ['legal', 'document', type],
    queryFn: () => legalService.getCurrentDocument(type),
    enabled,
  })
}
